import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../stores/authStore';
import useLang from '../hooks/useLang';

function formatMoney(v) {
  return new Intl.NumberFormat('sw-TZ').format(v || 0) + ' TZS';
}

const months_sw = ['Jan','Feb','Mac','Apr','Mei','Jun','Jul','Ago','Sep','Okt','Nov','Des'];
function formatDate(d) {
  const dt = new Date(d);
  return `${dt.getDate()} ${months_sw[dt.getMonth()]} ${dt.getFullYear()}`;
}

const GT_TYPES = ['Msiba', 'Harusi', 'Ugonjwa', 'Nyingine'];

const emptyForm = {
  member_id: '',
  sub_type: 'Msiba',
  amount: '',
  transaction_date: new Date().toISOString().slice(0, 10),
  notes: '',
};

function GTModal({ title, form, setForm, members, onSave, onClose, saving, error, T }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <form
        onSubmit={onSave}
        className="w-full max-w-lg rounded-[28px] border border-rose-200 bg-white shadow-[0_32px_80px_rgba(15,23,42,0.22)] overflow-hidden"
      >
        {/* Modal header */}
        <div className="bg-[linear-gradient(135deg,rgba(159,18,57,0.94),rgba(15,23,42,0.94))] px-6 py-4 flex items-center justify-between">
          <h2 className="font-semibold text-white text-base">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-white/15 w-8 h-8 flex items-center justify-center text-white hover:bg-white/25 transition-colors text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <p className="rounded-xl bg-red-100 px-4 py-2 text-sm text-red-700">{error}</p>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {/* Member */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-rose-700 mb-1">{T.gt.memberLabel}</label>
              <select
                value={form.member_id}
                onChange={e => setForm(f => ({ ...f, member_id: e.target.value }))}
                className="w-full rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-400"
                required
              >
                <option value="">{T.gt.memberSelect}</option>
                {members.map(m => (
                  <option key={m.id} value={m.id}>{m.name} ({m.phone})</option>
                ))}
              </select>
            </div>

            {/* Sub-type */}
            <div>
              <label className="block text-xs font-semibold text-rose-700 mb-1">{T.gt.subTypeLabel}</label>
              <select
                value={form.sub_type}
                onChange={e => setForm(f => ({ ...f, sub_type: e.target.value }))}
                className="w-full rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-400"
              >
                {GT_TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
              </select>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-xs font-semibold text-rose-700 mb-1">{T.gt.amountLabel}</label>
              <input
                type="number"
                min="1"
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder={T.gt.amountPlaceholder}
                className="w-full rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-400"
                required
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-xs font-semibold text-rose-700 mb-1">{T.gt.dateLabel}</label>
              <input
                type="date"
                value={form.transaction_date}
                onChange={e => setForm(f => ({ ...f, transaction_date: e.target.value }))}
                className="w-full rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-400"
                required
              />
            </div>

            {/* Notes */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-rose-700 mb-1">{T.gt.notesLabel}</label>
              <input
                type="text"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder={T.gt.notesPlaceholder}
                className="w-full rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-400"
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              {T.gt.cancel}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-rose-700 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-800 disabled:opacity-50 transition-colors"
            >
              {saving ? T.gt.saving : T.gt.save}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function GroupTransfers() {
  const { T } = useLang();
  const { id } = useParams();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [transfers, setTransfers] = useState([]);
  const [members, setMembers] = useState([]);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  // Add modal
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');

  // Edit modal
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    Promise.all([
      api.get(`/groups/${id}`),
      api.get(`/transactions?group_id=${id}&type=Group Transfer`),
      api.get(`/members?group_id=${id}`),
    ]).then(([g, t, m]) => {
      setGroup(g.data);
      setTransfers(t.data || []);
      setMembers((m.data || []).filter(mb => mb.status !== 'inactive'));
    }).finally(() => setLoading(false));
  }, [id]);

  const handleDelete = async (t) => {
    const name = t.linked_member_name || t.member_name;
    if (!confirm(`${T.gt.confirmDelete} ${name} (${formatMoney(t.amount)})?\n\n${T.gt.confirmDeleteMsg}`)) return;
    setDeleting(t.id);
    try {
      await api.delete(`/transactions/${t.id}`);
      setTransfers(prev => prev.filter(x => x.id !== t.id));
    } catch (err) {
      alert(err.response?.data?.error || T.gt.errDelete);
    } finally {
      setDeleting(null);
    }
  };

  const handleAddSave = async (e) => {
    e.preventDefault();
    setAddError('');
    if (!addForm.member_id) { setAddError(T.gt.errMember); return; }
    if (!addForm.amount || Number(addForm.amount) <= 0) { setAddError(T.gt.errAmount); return; }
    setAddSaving(true);
    try {
      const { data } = await api.post('/transactions', {
        group_id: Number(id),
        member_id: Number(addForm.member_id),
        amount: Number(addForm.amount),
        type: 'Group Transfer',
        sub_type: addForm.sub_type || 'Msiba',
        transaction_date: addForm.transaction_date,
        notes: addForm.notes || null,
      });
      const member = members.find(m => m.id === Number(addForm.member_id));
      setTransfers(prev => [{ ...data, linked_member_name: member?.name, member_phone: member?.phone }, ...prev]);
      setAddForm(emptyForm);
      setShowAdd(false);
    } catch (err) {
      setAddError(err.response?.data?.error || T.gt.errSave);
    } finally {
      setAddSaving(false);
    }
  };

  const openEdit = (t) => {
    setEditTarget(t);
    setEditForm({
      member_id: String(t.member_id),
      sub_type: t.sub_type || 'Msiba',
      amount: t.amount,
      transaction_date: t.transaction_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
      notes: t.notes || '',
    });
    setEditError('');
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setEditError('');
    if (!editForm.member_id) { setEditError(T.gt.errMember); return; }
    if (!editForm.amount || Number(editForm.amount) <= 0) { setEditError(T.gt.errAmount); return; }
    setEditSaving(true);
    try {
      const { data } = await api.patch(`/transactions/${editTarget.id}`, {
        member_id: Number(editForm.member_id),
        sub_type: editForm.sub_type,
        amount: Number(editForm.amount),
        transaction_date: editForm.transaction_date,
        notes: editForm.notes || null,
      });
      const member = members.find(m => m.id === Number(editForm.member_id));
      setTransfers(prev => prev.map(t =>
        t.id === editTarget.id
          ? { ...t, ...data, linked_member_name: member?.name ?? data.member_name, member_phone: member?.phone ?? data.member_phone }
          : t
      ));
      setEditTarget(null);
    } catch (err) {
      setEditError(err.response?.data?.error || T.gt.errEdit);
    } finally {
      setEditSaving(false);
    }
  };

  const total = transfers.reduce((s, t) => s + parseFloat(t.amount || 0), 0);

  if (loading) return <div className="text-center py-20 text-gray-500">{T.common.loading}</div>;

  const subTypeBadge = (st) => {
    const cls = st === 'Harusi'
      ? 'bg-pink-100 text-pink-700'
      : st === 'Msiba'
        ? 'bg-slate-100 text-slate-600'
        : st === 'Ugonjwa'
          ? 'bg-orange-100 text-orange-700'
          : 'bg-blue-50 text-blue-600';
    return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${cls}`}>{st}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Add modal */}
      {isAdmin && showAdd && (
        <GTModal
          title={T.gt.addModalTitle}
          form={addForm}
          setForm={setAddForm}
          members={members}
          onSave={handleAddSave}
          onClose={() => { setShowAdd(false); setAddError(''); }}
          saving={addSaving}
          error={addError}
          T={T}
        />
      )}

      {/* Edit modal */}
      {isAdmin && editTarget && (
        <GTModal
          title={T.gt.editModalTitle}
          form={editForm}
          setForm={setEditForm}
          members={members}
          onSave={handleEditSave}
          onClose={() => { setEditTarget(null); setEditError(''); }}
          saving={editSaving}
          error={editError}
          T={T}
        />
      )}

      {/* Header */}
      <div className="overflow-hidden rounded-[30px] border border-white/70 bg-[linear-gradient(135deg,rgba(159,18,57,0.94),rgba(15,23,42,0.94))] px-5 py-6 text-white shadow-[0_24px_60px_rgba(15,23,42,0.18)] sm:px-6">
        <Link
          to={`/groups/${id}`}
          className="inline-flex rounded-full bg-white/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-100"
        >
          ← {group?.name || 'Kikundi'}
        </Link>
        <div className="mt-4 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold sm:text-3xl">{T.gt.title}</h1>
            <p className="mt-1 text-sm text-rose-100/80">{T.gt.subtitle}</p>
          </div>
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[24px] bg-white/12 text-3xl shadow-inner shadow-white/10">
            🏥
          </div>
        </div>
        <div className="mt-4 rounded-2xl bg-white/10 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-200/70">{T.gt.totalDisbursed}</p>
          <p className="mt-1 text-2xl font-bold">{formatMoney(total)}</p>
          <p className="text-xs text-rose-100/60 mt-0.5">{transfers.length} {T.gt.payments}</p>
        </div>
      </div>

      {/* Add button */}
      {isAdmin && (
        <div className="flex justify-end">
          <button
            onClick={() => { setShowAdd(true); setAddForm(emptyForm); setAddError(''); }}
            className="flex items-center gap-2 rounded-2xl bg-rose-700 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-rose-800 transition-colors"
          >
            {T.gt.addBtn}
          </button>
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {transfers.length === 0 && (
          <div className="text-center py-12 text-gray-400">{T.gt.empty}</div>
        )}

        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {transfers.map((t) => (
            <div key={t.id} className="rounded-[24px] border border-white/75 bg-white/88 p-4 shadow-[0_14px_35px_rgba(15,23,42,0.08)]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={`/members/${t.member_id}`}
                    className="font-semibold text-slate-800 hover:text-rose-700 hover:underline block truncate"
                  >
                    {t.linked_member_name || t.member_name}
                  </Link>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-slate-400">{t.member_phone}</p>
                    {t.sub_type && subTypeBadge(t.sub_type)}
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-rose-50 px-3 py-1 text-sm font-bold text-rose-700">
                  {formatMoney(t.amount)}
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="rounded-2xl bg-slate-50 px-3 py-2 flex-1">
                  <p className="text-xs text-slate-400">{formatDate(t.transaction_date)}</p>
                  {t.notes && <p className="text-xs text-slate-500 mt-0.5 italic">{t.notes}</p>}
                </div>
                {isAdmin && (
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => openEdit(t)}
                      className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
                    >
                      {T.gt.edit}
                    </button>
                    <button
                      onClick={() => handleDelete(t)}
                      disabled={deleting === t.id}
                      className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-100 disabled:opacity-50 transition-colors"
                    >
                      {deleting === t.id ? '...' : T.gt.delete}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        {transfers.length > 0 && (
          <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-gray-500 text-xs uppercase">
                  <th className="text-left px-5 py-3">{T.gt.colMember}</th>
                  <th className="text-left px-5 py-3">{T.gt.colPhone}</th>
                  <th className="text-left px-5 py-3">{T.gt.colType}</th>
                  <th className="text-right px-5 py-3">{T.gt.colAmount}</th>
                  <th className="text-left px-5 py-3">{T.gt.colDate}</th>
                  <th className="text-left px-5 py-3">{T.gt.colNotes}</th>
                  {isAdmin && <th className="px-5 py-3" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transfers.map((t) => (
                  <tr key={t.id} className="hover:bg-rose-50/40 transition-colors">
                    <td className="px-5 py-3">
                      <Link to={`/members/${t.member_id}`} className="font-medium text-rose-700 hover:underline">
                        {t.linked_member_name || t.member_name}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-gray-500">{t.member_phone}</td>
                    <td className="px-5 py-3">{t.sub_type ? subTypeBadge(t.sub_type) : '—'}</td>
                    <td className="px-5 py-3 text-right font-bold text-rose-700">{formatMoney(t.amount)}</td>
                    <td className="px-5 py-3 text-gray-500">{formatDate(t.transaction_date)}</td>
                    <td className="px-5 py-3 text-gray-400 italic text-xs">{t.notes || '—'}</td>
                    {isAdmin && (
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={() => openEdit(t)}
                            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
                          >
                            {T.gt.edit}
                          </button>
                          <button
                            onClick={() => handleDelete(t)}
                            disabled={deleting === t.id}
                            className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-100 disabled:opacity-50 transition-colors"
                          >
                            {deleting === t.id ? '...' : T.gt.delete}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-rose-200 bg-rose-50">
                  <td colSpan={3} className="px-5 py-3 font-semibold text-rose-800">{T.gt.total}</td>
                  <td className="px-5 py-3 text-right font-bold text-rose-800 text-base">{formatMoney(total)}</td>
                  <td colSpan={isAdmin ? 3 : 2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
